import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Alert,
  Box,
  Typography,
  CircularProgress,
  Snackbar,
  InputAdornment,
  IconButton,
  Divider
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { userService } from '../../../services/api';

interface ResetPasswordFormProps {
  email: string;
  onPasswordReset: () => void;
  onBackToLogin: () => void;
}

const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({
  email,
  onPasswordReset,
  onBackToLogin
}) => {
  const { t } = useLocalization();
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState({
    new: false,
    confirm: false,
  });
  const [error, setError] = useState('');
  const [resendDisabled, setResendDisabled] = useState(true);
  const [countdown, setCountdown] = useState(60);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleTogglePasswordVisibility = (field: 'new' | 'confirm') => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0 && resendDisabled) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    } else if (countdown === 0) {
      setResendDisabled(false);
    }
    return () => clearInterval(timer);
  }, [countdown, resendDisabled]);

  // const handleResendOTP = async () => {
  //   try {
  //     setLoading(true);
  //     const response = await userService.forgotPassword(email);
  //     if (response.success) {
  //       setError('');
  //       setResendDisabled(true);
  //       setCountdown(60);
  //       setToast({
  //         open: true,
  //         message: t.auth.forgotPassword.success,
  //         severity: 'success'
  //       });
  //     } else {
  //       setError(response.message || t.auth.forgotPassword.error);
  //       setToast({
  //         open: true,
  //         message: response.message || t.auth.forgotPassword.error,
  //         severity: 'error'
  //       });
  //     }
  //   } catch (err: any) {
  //     setError(err.response?.data?.message || t.auth.forgotPassword.error);
  //     setToast({
  //       open: true,
  //       message: err.response?.data?.message || t.auth.forgotPassword.error,
  //       severity: 'error'
  //     });
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError(t.auth.changePassword.passwordsNotMatch);
      setToast({
        open: true,
        message: t.auth.changePassword.passwordsNotMatch,
        severity: 'error'
      });
      return;
    }
    try {
      setLoading(true);
      const response = await userService.resetPassword({
        email,
        otp,
        password: newPassword,
      });

      if (response.success) {
        setError('');
        setToast({
          open: true,
          message: t.auth.resetPassword.success,
          severity: 'success'
        });
        // Redirect to login after showing success message
        setTimeout(() => {
          onPasswordReset();
        }, 2000);
      } else {
        setError(response.message || t.auth.resetPassword.error);
        setToast({
          open: true,
          message: response.message || t.auth.resetPassword.error,
          severity: 'error'
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t.auth.resetPassword.error);
      setToast({
        open: true,
        message: err.response?.data?.message || t.auth.resetPassword.error,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  return (
    <Box>
      <form onSubmit={handleResetPassword}>
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            We've sent a verification code to <strong>{email}</strong>. Enter the code below to reset your password.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2 }}>
          <TextField
            fullWidth
            label={t.auth.resetPassword.otp}
            value={otp}
            onChange={(e) => {
              // Only allow numbers and limit to 6 digits
              const value = e.target.value.replace(/\D/g, '').slice(0, 6);
              setOtp(value);
            }}
            margin="normal"
            required
            placeholder="Enter 6-digit code"
            inputProps={{
              maxLength: 6,
              pattern: '[0-9]*'
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }}
          />
        </Box>

        <TextField
          fullWidth
          label={t.auth.changePassword.newPassword}
          type={showPasswords.new ? 'text' : 'password'}
          value={newPassword}
          placeholder="Enter your new password"
          onChange={(e) => setNewPassword(e.target.value)}
          margin="normal"
          required
          helperText={
            newPassword.length > 0 && newPassword.length < 6
              ? "Password must be at least 6 characters long"
              : "Create a strong password with at least 6 characters"
          }
          error={newPassword.length > 0 && newPassword.length < 6}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => handleTogglePasswordVisibility('new')}
                  edge="end"
                >
                  {showPasswords.new ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&:hover fieldset': {
                borderColor: 'primary.main',
              },
            },
          }}
        />

        <TextField
          fullWidth
          label={t.auth.changePassword.confirmPassword}
          type={showPasswords.confirm ? 'text' : 'password'}
          value={confirmPassword}
          placeholder="Confirm your new password"
          onChange={(e) => setConfirmPassword(e.target.value)}
          margin="normal"
          required
          helperText={
            confirmPassword.length > 0 && newPassword !== confirmPassword
              ? "Passwords do not match"
              : "Confirm your new password"
          }
          error={confirmPassword.length > 0 && newPassword !== confirmPassword}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => handleTogglePasswordVisibility('confirm')}
                  edge="end"
                >
                  {showPasswords.confirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&:hover fieldset': {
                borderColor: 'primary.main',
              },
            },
          }}
        />

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={
            loading ||
            !otp ||
            otp.length !== 6 ||
            !newPassword ||
            newPassword.length < 6 ||
            !confirmPassword ||
            newPassword !== confirmPassword
          }
          sx={{
            mt: 3,
            mb: 2,
            py: 1.5,
            borderRadius: 2,
            fontSize: '1.1rem',
            fontWeight: 'medium',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
            }
          }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t.auth.resetPassword.submit
          )}
        </Button>

        <Divider sx={{ my: 2 }}>
          <Typography variant="body2" color="text.secondary">
            or
          </Typography>
        </Divider>

        <Button
          variant="outlined"
          fullWidth
          onClick={onBackToLogin}
          startIcon={<ArrowBackIcon />}
          sx={{
            py: 1.5,
            borderRadius: 2,
            fontWeight: 'medium'
          }}
        >
          {t.common.back}
        </Button>
      </form>

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ResetPasswordForm; 